import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Download, FileSpreadsheet, FileText,
  TrendingUp, TrendingDown, Calendar, Euro, Loader2,
  Receipt, CreditCard, Banknote, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(value || 0);
};

const months = [
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'März' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Dezember' },
];

export default function AdminReports() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(null);
  const [yearlyOverview, setYearlyOverview] = useState([]);

  useEffect(() => {
    loadMonthReport();
    loadYearlyOverview();
  }, [selectedYear, selectedMonth]);

  const loadMonthReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/reports/month?year=${selectedYear}&month=${selectedMonth}`
      );
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Fehler beim Laden des Berichts');
    } finally {
      setLoading(false);
    }
  };

  const loadYearlyOverview = async () => {
    try {
      const promises = months.map(async (month) => {
        const response = await fetch(
          `${API_URL}/api/reports/month?year=${selectedYear}&month=${month.value}`
        );
        if (response.ok) {
          const data = await response.json();
          return {
            month: month.value,
            label: month.label,
            total: data.total_gross || 0,
            sales: data.sales_count || 0
          };
        }
        return { month: month.value, label: month.label, total: 0, sales: 0 };
      });
      
      const results = await Promise.all(promises);
      setYearlyOverview(results);
    } catch (error) {
      console.error('Error loading yearly overview:', error);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    
    try {
      // Calculate start and end of selected month
      const startDate = new Date(selectedYear, selectedMonth - 1, 1);
      const endDate = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);
      
      const response = await fetch(`${API_URL}/api/exports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          format: format,
          include_open_days: true
        })
      });
      
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      
      const data = await response.json();
      
      // Download file
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', data.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${format.toUpperCase()} Export erstellt`, {
        description: `${data.closed_count + data.open_count} Buchungen exportiert`
      });
    } catch (error) {
      toast.error('Export fehlgeschlagen', { description: error.message });
    } finally {
      setExporting(null);
    }
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  };

  const maxMonthlyTotal = Math.max(...yearlyOverview.map(m => m.total), 1);

  if (loading && !report) {
    return (
      <div className="p-6 md:p-8">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-slate-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold flex items-center gap-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-reports-title"
        >
          <BarChart3 className="h-8 w-8 text-emerald-400" />
          Reporting & Export
        </h1>
        <p className="text-slate-400 mt-1">Umsatzberichte und Datenexporte</p>
      </div>

      {/* Period Selection */}
      <Card 
        className="p-4 mb-6 flex items-center gap-4 flex-wrap"
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-slate-400" />
          <span className="text-sm font-medium">Zeitraum:</span>
        </div>
        
        <Select 
          value={selectedMonth.toString()} 
          onValueChange={(v) => setSelectedMonth(parseInt(v))}
        >
          <SelectTrigger className="w-40 bg-[#0E1628] border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111C32] border-slate-700">
            {months.map((month) => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select 
          value={selectedYear.toString()} 
          onValueChange={(v) => setSelectedYear(parseInt(v))}
        >
          <SelectTrigger className="w-28 bg-[#0E1628] border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111C32] border-slate-700">
            {getYears().map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex gap-2">
          <Button
            onClick={() => handleExport('csv')}
            disabled={exporting === 'csv'}
            variant="outline"
            className="border-slate-600 hover:bg-slate-700"
            data-testid="export-csv-btn"
          >
            {exporting === 'csv' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-400" />
            )}
            CSV Export
          </Button>
          <Button
            onClick={() => handleExport('datev')}
            disabled={exporting === 'datev'}
            variant="outline"
            className="border-slate-600 hover:bg-slate-700"
            data-testid="export-datev-btn"
          >
            {exporting === 'datev' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2 text-blue-400" />
            )}
            DATEV Export
          </Button>
        </div>
      </Card>

      {/* Monthly Stats */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total Revenue */}
          <Card 
            className="p-5"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
            data-testid="stat-total-revenue"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Gesamtumsatz</span>
              <Euro className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-emerald-400">
              {formatCurrency(report.total_gross)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {report.sales_count} Verkäufe
            </div>
          </Card>

          {/* Cash */}
          <Card 
            className="p-5"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
            data-testid="stat-cash"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Bargeld</span>
              <Banknote className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="text-2xl font-bold text-yellow-400">
              {formatCurrency(report.cash_total)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {report.total_gross > 0 
                ? `${((report.cash_total / report.total_gross) * 100).toFixed(1)}%`
                : '0%'
              }
            </div>
          </Card>

          {/* Card */}
          <Card 
            className="p-5"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
            data-testid="stat-card"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Kartenzahlung</span>
              <CreditCard className="h-5 w-5 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-blue-400">
              {formatCurrency(report.card_total)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {report.total_gross > 0 
                ? `${((report.card_total / report.total_gross) * 100).toFixed(1)}%`
                : '0%'
              }
            </div>
          </Card>

          {/* VAT */}
          <Card 
            className="p-5"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
            data-testid="stat-vat"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">MwSt gesamt</span>
              <Receipt className="h-5 w-5 text-purple-400" />
            </div>
            <div className="text-2xl font-bold text-purple-400">
              {formatCurrency(report.total_vat)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Netto: {formatCurrency(report.total_net)}
            </div>
          </Card>
        </div>
      )}

      {/* VAT Breakdown */}
      {report && report.vat_breakdown && Object.keys(report.vat_breakdown).length > 0 && (
        <Card 
          className="p-5 mb-6"
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <h3 className="text-lg font-semibold mb-4">MwSt nach Satz</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(report.vat_breakdown).map(([rate, amount]) => (
              <div key={rate} className="p-3 rounded-lg bg-[#0E1628]">
                <div className="text-sm text-slate-400">{rate}% MwSt</div>
                <div className="text-xl font-bold">{formatCurrency(amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Yearly Overview */}
      <Card 
        className="p-5 mb-6"
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Jahresübersicht {selectedYear}</h3>
          <Badge variant="outline" className="border-slate-600">
            {yearlyOverview.reduce((sum, m) => sum + m.sales, 0)} Verkäufe
          </Badge>
        </div>
        
        <div className="grid grid-cols-12 gap-2">
          {yearlyOverview.map((month) => (
            <div 
              key={month.month}
              className="flex flex-col items-center cursor-pointer group"
              onClick={() => setSelectedMonth(month.month)}
            >
              {/* Bar */}
              <div 
                className="w-full rounded-t transition-all group-hover:opacity-80"
                style={{ 
                  height: '120px',
                  background: `linear-gradient(to top, ${
                    month.month === selectedMonth 
                      ? '#2F80FF' 
                      : '#1E293B'
                  } ${(month.total / maxMonthlyTotal) * 100}%, transparent 0%)`
                }}
              />
              {/* Label */}
              <div className={`text-xs mt-2 ${
                month.month === selectedMonth ? 'text-[#2F80FF] font-bold' : 'text-slate-500'
              }`}>
                {month.label.substring(0, 3)}
              </div>
              {/* Amount */}
              <div className="text-xs text-slate-400 mt-1">
                {formatCurrency(month.total)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Daily Breakdown */}
      {report && report.daily_breakdown && report.daily_breakdown.length > 0 && (
        <Card 
          className="p-5"
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <h3 className="text-lg font-semibold mb-4">Tägliche Aufschlüsselung</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="daily-breakdown-table">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-400">Datum</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-400">Verkäufe</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-400">Umsatz</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-400">Bar</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-400">Karte</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-400">Stornos</th>
                </tr>
              </thead>
              <tbody>
                {report.daily_breakdown.map((day) => (
                  <tr key={day.date} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="py-3 px-4 font-medium">
                      {new Date(day.date + 'T00:00:00').toLocaleDateString('de-DE', {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="text-right py-3 px-4">{day.sales}</td>
                    <td className="text-right py-3 px-4 font-bold text-emerald-400">
                      {formatCurrency(day.gross)}
                    </td>
                    <td className="text-right py-3 px-4 text-yellow-400">
                      {formatCurrency(day.cash)}
                    </td>
                    <td className="text-right py-3 px-4 text-blue-400">
                      {formatCurrency(day.card)}
                    </td>
                    <td className="text-right py-3 px-4 text-red-400">
                      {day.refunds}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* No Data State */}
      {report && (!report.daily_breakdown || report.daily_breakdown.length === 0) && (
        <Card 
          className="p-12 text-center"
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <BarChart3 className="h-16 w-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Keine Daten verfügbar</h3>
          <p className="text-slate-400">
            Für {months.find(m => m.value === selectedMonth)?.label} {selectedYear} liegen keine Verkäufe vor.
          </p>
        </Card>
      )}
    </div>
  );
}
